import Icon, { CloseOutlined, PlusOutlined } from '@ant-design/icons';
import { Form, Tabs } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import FormField from 'components/FormField';
import { getSegmentsLength } from 'components/TemplateEditor/Editor';
import { mustache2SlateValuePolicyMsgTypes } from 'components/TemplateEditor/mustacheUtils';
import type { MsgType } from 'components/TemplateEditor/mustacheUtils';
import useSmartField from 'hooks/useSmartField';
import { set } from 'lodash-es';
import { platformExecuteContentLimitLengthMap } from 'services/config/platform';
import { Container } from './BilingualExecuteContextField';
import { Validate, useLangTemplatesMap } from './share';
import { FV } from '../NewPolicy/share';
import ExecuteContextsFieldV2, { getMsgLength } from './ExecuteContextsFieldV2';
import { CancelledTitle } from './CancelTradeContentsField';
import { useGetConcepts, ExtraTabs } from './TranslateTool';
import {
  isCancelledTradeScenarioType,
  isReturnTradeScenarioType,
  lazadaUnLimitMessageCountScenarios,
} from '../config';
import { ReactComponent as CancelledSvg } from '../assets/cancelled.svg';
import { DisabledProps } from '../TaskForm/share';
import { Concept } from 'components/TemplateEditor/toolbarPlugins/ConceptMenu';
import { regionLanguageMap } from 'services/config/region';
import toLowerCase from 'utils/toLowerCase';
import { useFormikContext } from 'formik';
import { isTiKTokUpperCase } from 'lib/utils/isTikTok';

export const validate: (
  ...params: [...Parameters<Validate>, General.PlatformUpperCase | false, Marketing.AllScenarioType]
) => void = (errors, values, t, platform, scenarioType) => {
  const { bilingualExecuteContexts = [] } = values;

  if (bilingualExecuteContexts.length === 0) {
    errors.bilingualExecuteContexts = t('refundSetting.请输入回复内容');
  } else {
    const contentLimit =
      isTiKTokUpperCase(platform) &&
      (scenarioType === 'NEGATIVE_REVIEW_REPLY' || scenarioType === 'POSITIVE_REVIEW_REPLY')
        ? 300
        : platform
        ? platformExecuteContentLimitLengthMap[platform]
        : Math.min(...Object.values(platformExecuteContentLimitLengthMap));
    bilingualExecuteContexts.forEach(({ content, translatedContent }, index) => {
      if (platform === 'LAZADA' && !lazadaUnLimitMessageCountScenarios.includes(scenarioType)) {
        const msgLength = getMsgLength(content);

        if (msgLength > 5) {
          set(errors, `bilingualExecuteContexts.${index}`, {
            text: t('aide.发送消息不可超过 5 条'),
          });
        }
      }

      const hasText = content.some((v) => v.type === 'paragraph');
      if (hasText) {
        const contentLength = getSegmentsLength(content, 'text');
        const translatedContentLength = getSegmentsLength(translatedContent, 'text');
        if (contentLength === 0) {
          set(errors, `bilingualExecuteContexts.${index}`, {
            text: t('refundSetting.请输入回复内容'),
          });
        } else if (contentLength > contentLimit || translatedContentLength > contentLimit) {
          set(errors, `bilingualExecuteContexts.${index}`, {
            text: t('refundSetting.回复内容不可超出x字', { x: contentLimit }),
          });
        }
      } else {
        const contentLength = getSegmentsLength(content, 'image');
        if (contentLength === 0) {
          set(errors, `bilingualExecuteContexts.${index}`, {
            text: t('refundSetting.请输入回复内容'),
          });
        }
      }
    });
  }
};

const supportRandomContextsScenarioTypes: Marketing.AllScenarioType[] = [
  'POSITIVE_REVIEW_REPLY',
  'NEGATIVE_REVIEW_REPLY',
];

const randomCountLimit = 10;

export interface BilingualExecuteContextsFieldProps {
  scenarioType: Marketing.AllScenarioType;
  region: General.UpperCaseRegion;
  platform?: General.PlatformUpperCase;
  msgTypes: MsgType[];
  replyOption?: Marketing.ReplyOption;
  supportPreview?: boolean;
  showMsgTypes?: boolean;
  allowDividing?: boolean;
  extraConcepts?: Concept[];
  extra?: (index: number) => JSX.Element;
  copyTip?: string;
  round?: number;
}

function BilingualExecuteContextsFieldV2(
  props: BilingualExecuteContextsFieldProps & DisabledProps,
) {
  const {
    scenarioType,
    region,
    platform,
    msgTypes,
    disabled = false,
    replyOption,
    supportPreview,
    showMsgTypes,
    extraConcepts,
    extra,
    copyTip,
    round = 1,
  } = props;

  const { t } = useTranslate();

  const langTemplatesMap = useLangTemplatesMap({
    scenarioType,
    round,
    platform: platform!,
  });

  const { [toLowerCase(region)]: regionLang } = regionLanguageMap;

  const newRecommendTemplates = langTemplatesMap?.[regionLang === 'tl' ? 'ph' : regionLang];

  const {
    field: { value: list, onChange: onListChange },
  } = useSmartField<FV['bilingualExecuteContexts']>('bilingualExecuteContexts');

  const {
    values: { recommendTemplates, knowledgeId, contentItemList },
  } = useFormikContext<FV>();

  const concepts = useGetConcepts(platform || false, scenarioType, replyOption, extraConcepts);

  const contextRender = (index: number) => {
    if (index >= list.length) {
      return null;
    }

    return (
      <FormField<Util.ElementType<FV['bilingualExecuteContexts']>>
        name={`bilingualExecuteContexts.${index}`}
      >
        {({ field: { value, onChange }, meta: { touched, error } }) => {
          const editor = (
            <ExecuteContextsFieldV2
              scenarioType={scenarioType}
              platform={platform}
              region={region}
              msgTypes={msgTypes}
              value={value}
              onChange={onChange}
              error={touched ? error : undefined}
              recommendTemplates={recommendTemplates}
              langTemplatesMap={langTemplatesMap}
              showMsgTypes={showMsgTypes}
              extraConcepts={extraConcepts}
              extra={extra && extra(index)}
              isKnowledge={Boolean(knowledgeId)}
              contentItemList={contentItemList}
            />
          );

          return (
            <Container>
              <Form.Item style={disabled ? { pointerEvents: 'none', cursor: 'not-allowed' } : {}}>
                {platform === 'SHOPEE' && isCancelledTradeScenarioType(scenarioType) && (
                  <CancelledTitle>
                    <Icon component={CancelledSvg} />
                    {t('aide.买家提交的取消订单申请已通过')}
                  </CancelledTitle>
                )}
                {editor}
              </Form.Item>
              <span>
                <ExtraTabs
                  platform={platform}
                  region={region}
                  concepts={concepts}
                  allowDividing
                  copyTip={copyTip}
                  supportPreview={supportPreview}
                  recommendTemplates={recommendTemplates}
                  newRecommendTemplates={newRecommendTemplates}
                  value={value}
                  onChange={disabled ? undefined : onChange}
                  systemContent={(() => {
                    if (isReturnTradeScenarioType(scenarioType)) {
                      return t('aide.买家申请仅退款/退货退款，待卖家处理');
                    }

                    if (isCancelledTradeScenarioType(scenarioType)) {
                      return t('aide.买家申请取消订单，申请已通过');
                    }

                    return undefined;
                  })()}
                />
              </span>
            </Container>
          );
        }}
      </FormField>
    );
  };

  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (activeIndex >= list.length) {
      setActiveIndex(0);
    }
  }, [activeIndex, list.length]);

  if (!supportRandomContextsScenarioTypes.includes(scenarioType)) {
    return contextRender(0);
  }

  const appendKey = '@append';

  return (
    <Tabs
      type="card"
      activeKey={`${activeIndex}`}
      onChange={(key) => {
        if (key === appendKey) {
          onListChange([
            ...list,
            {
              content: mustache2SlateValuePolicyMsgTypes(''),
              translatedContent: mustache2SlateValuePolicyMsgTypes(''),
            },
          ]);
          setActiveIndex(list.length);
        } else {
          setActiveIndex(parseInt(key, 10));
        }
      }}
    >
      {list.map((_, index) => {
        return (
          <Tabs.TabPane
            // eslint-disable-next-line react/no-array-index-key
            key={`${index}`}
            tab={
              <div>
                {t('aide.随机发送内容index', { index: index + 1 })}
                {list.length > 1 && !disabled && (
                  <CloseOutlined
                    style={{ margin: '0 0 0 12px' }}
                    onClick={(e) => {
                      e.stopPropagation();

                      onListChange([...list.slice(0, index), ...list.slice(index + 1)]);
                    }}
                  />
                )}
              </div>
            }
          >
            {contextRender(index)}
          </Tabs.TabPane>
        );
      })}
      {list.length < randomCountLimit && !disabled && (
        <Tabs.TabPane
          key={appendKey}
          tab={
            <div>
              <PlusOutlined />
              <span>{t('aide.新增随机发送内容')}</span>
            </div>
          }
        />
      )}
    </Tabs>
  );
}

export default BilingualExecuteContextsFieldV2;
